import { FormEvent, useEffect, useMemo, useState } from "react";
import Button from "@material-ui/core/Button";
import { FileCopy } from "@material-ui/icons";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import { useTotemStyles } from "../../styles";
import { useGuicheDeleteStyles } from "./styles";
import { Totem } from "../../../../@types/totem";
import { useTotemUpdate } from "../../../../services/api-tansquery/totem/update";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
    totem: Totem;
    buttonTrigger?: React.ReactNode;
}

export default function TotemActiveToggle({ totem, buttonTrigger }: Props) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const { classes } = useGuicheDeleteStyles();
    const { classes: defaultClasses } = useTotemStyles();
    const mutation = useTotemUpdate(totem?.id, {
        onSuccess: async () => {
            setOpen(false);
            toast.success(`Totem ${!totem?.ativo ? "ativado" : "inativado"} com sucesso!`);
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ["totem", "list"]
                }),
                queryClient.invalidateQueries({
                    queryKey: ["totem", "details", totem?.id]
                })
            ]);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || "Algo deu errado, tente novamente!");
            console.error(error);
        }
    });

    const totemStatus = useMemo(() => {
        return totem?.ativo ? "Inativar" : "Ativar";
    }, [totem?.ativo]);

    async function handleSubmit(event: FormEvent<HTMLDivElement>) {
        event.preventDefault();

        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            token: totem?.token,
            senha_reset: totem?.senha_reset,
            ativo: !totem?.ativo,
            descricao: totem?.descricao,
            cor_fonte: totem?.cor_fonte,
            cor_fundo: totem?.cor_fundo,
            local_atendimento: totem?.local_atendimento.slug,
            orgao: totem?.orgao.id,
            servicos: totem?.servicos.map(item => item.id.toString()),
            prioridades: totem?.prioridades.map(item => item.id.toString()),
            logo: totem?.logo || "",
        });
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<FileCopy />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    {totemStatus} Totem
                </Button>
            )}
            <Dialog open={open} maxWidth="sm">
                <Card component="form" onSubmit={handleSubmit}>
                    <CardHeader
                        avatar={<FileCopy />}
                        className={classes.cardHeader}
                        title={
                            <Typography variant="h5">
                                {totemStatus} Totem
                            </Typography>
                        }
                    />
                    <CardContent className={classes.cardContent}>
                        <Typography className={classes.textWarning}>
                            Você está prestes a <strong>{totemStatus}</strong> o totem <strong>{totem?.descricao}</strong> do sistema.
                        </Typography>
                        <Typography className={classes.textWarning}>
                            Tem certeza que deseja continuar?
                        </Typography>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={classes.duplicateButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? (
                                !totem?.ativo ? "Ativando..." : "Inativando..."
                            ) : (
                                !totem?.ativo ? "Ativar" : "Inativar"
                            )}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
